import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Activity, Boxes, Network } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/89aa8e73-5a6c-41e7-aa52-5d2f193d3e77/images/c24f38ad016636604d6f451f1ecf217663c52efc43ac6c373e3befaddcd80cd5.png";
const DASH_IMG =
  "https://static.prod-images.emergentagent.com/jobs/89aa8e73-5a6c-41e7-aa52-5d2f193d3e77/images/cc9b8d2e72eddbeeb0b327e6879b70d1fe01bf9eeed24ffd8f4d0e1f69885f92.png";

export default function Hero() {
  const { t } = useLang();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Decorative dot grid */}
      <div className="absolute inset-0 bg-dot-grid opacity-60 pointer-events-none" aria-hidden="true" />
      <div className="absolute -top-20 -right-32 w-[42rem] h-[42rem] rounded-full bg-[radial-gradient(closest-side,rgba(30,91,214,0.18),transparent_70%)] pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#1E5BD6]">
              <span className="h-px w-8 bg-[#1E5BD6]" />
              {t.hero.eyebrow}
            </div>

            <h1
              data-testid="hero-title"
              className="mt-6 font-heading font-extrabold text-slate-900 text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight"
            >
              {t.hero.title1}
              <span className="relative inline-block">
                <span className="text-[#1E5BD6]">{t.hero.title_accent}</span>
                <span
                  className="absolute left-0 right-0 -bottom-1 h-[3px] bg-[#1E5BD6]/30"
                  aria-hidden="true"
                />
              </span>
              {t.hero.title2}
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl"
            >
              {t.hero.subtitle}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-sm bg-[#1E5BD6] hover:bg-[#164AA8] text-white font-semibold text-sm tracking-wide transition-colors"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-sm border border-slate-300 hover:border-[#1E5BD6] hover:text-[#1E5BD6] text-slate-700 font-semibold text-sm tracking-wide transition-colors"
              >
                {t.hero.ctaSecondary}
              </a>
            </div>

            <dl className="mt-12 grid grid-cols-3 gap-4 sm:gap-8 max-w-xl border-t border-slate-200 pt-8">
              {[
                { icon: Network, value: t.hero.stat1 },
                { icon: Activity, value: t.hero.stat2 },
                { icon: Boxes, value: t.hero.stat3 },
              ].map((item, i) => (
                <div key={i} className="flex flex-col gap-2" data-testid={`hero-stat-${i}`}>
                  <item.icon className="h-4 w-4 text-[#1E5BD6]" />
                  <dt className="text-xs sm:text-sm font-semibold text-slate-900 leading-tight">
                    {item.value}
                  </dt>
                </div>
              ))}
            </dl>
          </motion.div>

          {/* Right visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden ring-1 ring-slate-200 shadow-2xl shadow-blue-900/10 bg-[#0B1120]">
              <img
                src={HERO_IMG}
                alt="Digital operational transformation"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-transparent to-transparent" />

              {/* Corner accents */}
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <span className="text-[10px] tracking-[0.3em] text-slate-300 uppercase">
                  Live operations
                </span>
                <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  ONLINE
                </span>
              </div>
            </div>

            {/* Floating dashboard card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hidden md:block absolute -bottom-8 -left-10 w-[78%] rounded-sm overflow-hidden ring-1 ring-slate-200 bg-white shadow-2xl"
              aria-hidden="true"
            >
              <img
                src={DASH_IMG}
                alt=""
                className="block w-full h-auto"
                loading="lazy"
              />
            </motion.div>

            {/* Small floating stat */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="hidden md:flex absolute -top-6 -right-4 flex-col items-start gap-1 bg-white border border-slate-200 rounded-sm px-4 py-3 shadow-lg"
            >
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                Uptime
              </span>
              <span className="font-heading font-extrabold text-2xl text-slate-900">99.95%</span>
              <span className="text-[11px] text-emerald-600">+0.04 vs LM</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
